"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NumberFormatterServiceV1 = void 0;
const createIntlNumberFormat_1 = require("./utils/createIntlNumberFormat");
class NumberFormatterServiceV1 {
    constructor(config, logger) {
        this.logger = logger;
        try {
            this.numberFormatter = (0, createIntlNumberFormat_1.createNumberFormatter)(config, logger);
            this.currencyFormatter = (0, createIntlNumberFormat_1.createCurrencyFormatter)(config, logger);
            this.isSpanishLocale = config.language.toLowerCase() === "es";
            this.isPolishLocale = config.language.toLowerCase() === "pl";
            this.isSwissFrenchLocale =
                config.language.toLowerCase() === "fr" &&
                    config.countryCode.toUpperCase() === "CH";
        }
        catch (err) {
            logger?.error(`Error initializing formatters: ${err}`);
            throw err;
        }
    }
    getGroupSeparator(formatter, isCurrency) {
        const cache = isCurrency
            ? this.currencyGroupSeparatorCache
            : this.numberGroupSeparatorCache;
        if (cache !== undefined) {
            return cache;
        }
        const parts = formatter.formatToParts(12345);
        const groupPart = parts.find((part) => part.type === "group");
        const separator = groupPart?.value ?? null;
        if (separator === null) {
            this.logger?.warn(`No group separator found for ${isCurrency ? "currency" : "number"} formatter when formatting 10000. ` +
                `Parts: ${JSON.stringify(parts.map((p) => ({ type: p.type, value: p.value })))}`);
        }
        if (isCurrency) {
            this.currencyGroupSeparatorCache = separator;
        }
        else {
            this.numberGroupSeparatorCache = separator;
        }
        return separator;
    }
    postProcessFormat(value, formatted) {
        if (this.isSpanishLocale || this.isPolishLocale) {
            return this.apply4DigitGrouping(value, formatted, this.numberFormatter);
        }
        return formatted;
    }
    postProcessCurrencyFormat(value, formatted) {
        let result = formatted;
        if (this.isSpanishLocale || this.isPolishLocale) {
            result = this.apply4DigitGrouping(value, result, this.currencyFormatter);
        }
        if (this.isSwissFrenchLocale) {
            result = this.applySwissFrenchCurrencyPosition(value, result);
        }
        return result;
    }
    apply4DigitGrouping(value, formatted, formatter) {
        const absValue = Math.abs(value);
        if (absValue < 1000 || absValue >= 10000) {
            return formatted;
        }
        const parts = formatter.formatToParts(value);
        const hasGrouping = parts.some((part) => part.type === "group");
        if (hasGrouping) {
            return formatted;
        }
        const integerPartIndex = parts.findIndex((part) => part.type === "integer");
        if (integerPartIndex === -1) {
            this.logger?.warn(`4-digit grouping: No integer part found in formatted number. ` +
                `Value: ${value}, Formatted: "${formatted}", Parts: ${JSON.stringify(parts.map((p) => ({ type: p.type, value: p.value })))}`);
            return formatted;
        }
        if (parts[integerPartIndex].value.length !== 4) {
            return formatted;
        }
        const isCurrency = formatter === this.currencyFormatter;
        const groupSeparator = this.getGroupSeparator(formatter, isCurrency);
        if (groupSeparator === null) {
            this.logger?.warn(`4-digit grouping: No group separator available for locale. ` +
                `Value: ${value}, Formatted: "${formatted}"`);
            return formatted;
        }
        const integerValue = parts[integerPartIndex].value;
        const transformedParts = [
            ...parts.slice(0, integerPartIndex),
            { type: "integer", value: integerValue[0] },
            { type: "group", value: groupSeparator },
            { type: "integer", value: integerValue.slice(1) },
            ...parts.slice(integerPartIndex + 1),
        ];
        return transformedParts.map((part) => part.value).join("");
    }
    applySwissFrenchCurrencyPosition(value, formatted) {
        const parts = this.currencyFormatter.formatToParts(value);
        const currencyIndex = parts.findIndex((part) => part.type === "currency");
        if (currencyIndex === -1) {
            this.logger?.warn(`Swiss French currency: No currency part found in formatted currency. ` +
                `Value: ${value}, Formatted: "${formatted}", Parts: ${JSON.stringify(parts.map((p) => ({ type: p.type, value: p.value })))}`);
            return formatted;
        }
        const hasNumericPartsAfterCurrency = parts
            .slice(currencyIndex + 1)
            .some((part) => part.type === "integer" ||
            part.type === "fraction" ||
            part.type === "decimal");
        if (hasNumericPartsAfterCurrency) {
            return formatted;
        }
        const currencyPart = parts[currencyIndex];
        const spacingIndex = currencyIndex > 0 && parts[currencyIndex - 1].type === "literal"
            ? currencyIndex - 1
            : -1;
        const transformedParts = [
            currencyPart,
            ...(spacingIndex !== -1 ? [parts[spacingIndex]] : []),
            ...parts.slice(0, spacingIndex !== -1 ? spacingIndex : currencyIndex),
            ...parts.slice(currencyIndex + 1),
        ];
        return transformedParts.map((part) => part.value).join("");
    }
    formatNumber(value) {
        const formatted = this.numberFormatter.format(value);
        return this.postProcessFormat(value, formatted);
    }
    formatCurrency(value) {
        const formatted = this.currencyFormatter.format(value);
        return this.postProcessCurrencyFormat(value, formatted);
    }
}
exports.NumberFormatterServiceV1 = NumberFormatterServiceV1;
//# sourceMappingURL=v1.js.map